<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen p-4">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        @click="closeModal"
      ></div>

      <!-- 模态框内容 -->
      <div class="relative bg-white rounded-lg shadow-xl transform transition-all w-full max-w-lg p-6">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">
            修改密码
          </h3>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- 密码修改表单 -->
        <form @submit.prevent="changePassword" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              当前密码 <span class="text-red-500">*</span>
            </label>
            <input
              v-model="passwordForm.current_password"
              type="password"
              required
              class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              placeholder="请输入当前密码"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              新密码 <span class="text-red-500">*</span>
            </label>
            <input
              v-model="passwordForm.new_password"
              type="password"
              required
              minlength="8"
              class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              placeholder="请输入新密码（至少8位）"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              确认新密码 <span class="text-red-500">*</span>
            </label>
            <input
              v-model="passwordForm.confirm_password"
              type="password"
              required
              class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              placeholder="请再次输入新密码"
            />
          </div>

          <!-- 操作按钮 -->
          <div class="flex flex-col sm:flex-row justify-end gap-3 sm:space-x-3 sm:gap-0 pt-4 border-t border-gray-200">
            <button
              @click="closeModal"
              type="button"
              class="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors order-2 sm:order-1"
            >
              取消
            </button>
            
            <button 
              type="submit" 
              :disabled="isChangingPassword"
              class="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors order-1 sm:order-2"
            >
              <svg v-if="isChangingPassword" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ isChangingPassword ? '修改中...' : '确认修改' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'
import { XMarkIcon } from '@heroicons/vue/24/outline'

// Props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['close'])

// Composables
const authStore = useAuthStore()
const toast = useToast()

// State
const isChangingPassword = ref(false)

// 密码修改表单
const passwordForm = ref({
  current_password: '',
  new_password: '',
  confirm_password: ''
})

// Watch for modal show/hide
watch(() => props.show, (show) => {
  if (show) {
    resetForm()
  }
})

// Methods
const resetForm = () => {
  passwordForm.value = {
    current_password: '',
    new_password: '',
    confirm_password: ''
  }
  isChangingPassword.value = false
}

const changePassword = async () => {
  if (isChangingPassword.value) return
  
  if (passwordForm.value.new_password !== passwordForm.value.confirm_password) {
    toast.error('两次输入的密码不一致')
    return
  }
  
  if (passwordForm.value.new_password.length < 8) {
    toast.error('新密码至少需要8位字符')
    return
  }
  
  isChangingPassword.value = true
  
  try {
    await authStore.changePassword(passwordForm.value)
    // toast提示已在authStore中处理
    // 清空密码表单
    passwordForm.value = {
      current_password: '',
      new_password: '',
      confirm_password: ''
    }
    // 成功后先重置状态，再关闭模态框
    isChangingPassword.value = false
    closeModal()
  } catch (error) {
    console.error('修改密码失败:', error)
    toast.error('修改密码失败，请检查当前密码是否正确')
    isChangingPassword.value = false
  }
}

const closeModal = () => {
  if (!isChangingPassword.value) {
    emit('close')
  }
}
</script>